import React, { useState, useEffect, useMemo } from "react";
import { StyleSheet } from "react-native";
import { TextInput, useTheme, Paragraph, Button } from "react-native-paper";
import { useDispatch, useSelector } from "react-redux";

import { useTranslation } from "react-i18next";
import { RegisterOptions, useForm } from "react-hook-form";
import { ACCOUNT, EBINQUIRY, RESPONSEDATA } from "app/shared/services/services-model.type";
import { useDesktopMediaQuery } from "app/shared/util/media-query";
import { getTransferStockMakeFeeSelector, getTransferStockResBranchSelector, getTransferStockStoreSelector } from "../../reducer/selectors";
import {
  nonAccentVietnamese,
  regexCharacterMark,
  regexCharacterMarkSpaceDot,
  replaceEnterKey,
  replaceSpace,
} from "app/shared/util/validate";
import { DataTransferCreate } from "app/modules/transfer/reducer/types";
import { Constant } from "app/shared/constant/constant";
import { amountConfig, getListStock, getStock, getStockListBranch } from "app/modules/transfer/transfer-utils/transfer.utils";
import { PROFILE_BENE } from "app/modules/beneficiaries/utils/bene.utils";
import { spacing } from "app/shared/styles/spacing";
import { Block } from "app/shared/components/blocks/block";
import IconsYellowBanking from "app/shared/components/icons/icon-yellow-banking/IconsYellowBanking";
import { InputText, InputTextCurrency, InputAutoComplete, InputTextMultiline } from "app/shared/components/form/input";
import { StackSpacing, QueueSpacing } from "@components/spacing";
import { FeeDetailModalNew } from "app/shared/components/modal/make-fee/fee-detail.modal";
import FormViewFee from "app/shared/components/form/fee/form-fee";
import { fetchTransferStockBranchRequest } from "../../reducer/actions";
import BeneModal from "app/shared/components/modal/bene-account-source/select-bene.modal";
import { resetListBene } from "app/modules/beneficiaries/reducer/actions";
import { ItemCompleteProps } from "app/shared/components/modal/auto-complete/auto-complete";
import { getLimitRemainSelector } from "app/modules/account-management/reducer/selectors";

export interface FormTransStockProps {
  accountDebit: ACCOUNT;
  transferInfo?: RESPONSEDATA;
  onSubmit: Function;
  onCancel: Function;
  requestMakeFee: Function;
  requestCreate: Function;
  showModal: Function;
  closeModal: Function;
}

enum FormEnum {
  accountNo,
  accountName,
  stockID,
  stockName,
  branchCode,
  branchName,
  amount,
  description,
}

type ValidDataSchemeFormData = {
  [key in keyof typeof FormEnum]: Omit<RegisterOptions, "valueAsNumber" | "valueAsDate" | "setValueAs">;
};

export interface TransStockData {
  accountNo: string;
  accountName: string;
  stockID: string;
  stockName: string;
  branchCode: string;
  branchName: string;
  amount: string;
  description: string;
}

const FormTransStock = (props: FormTransStockProps) => {
  const { accountDebit, transferInfo, onSubmit, requestMakeFee, requestCreate, onCancel, showModal, closeModal } = props;
  const { colors } = useTheme();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isDesktop = useDesktopMediaQuery();

  const transferStockStore = useSelector(getTransferStockStoreSelector);
  const branchStore = useSelector(getTransferStockResBranchSelector);
  const limitRemainStore = useSelector(getLimitRemainSelector);
  const responseMakeFee = useSelector(getTransferStockMakeFeeSelector);

  const [validateAmount, setValidateAmount] = useState({ maxAmount: 0, errorMaxDebit: "" });
  const [listStock, setListStock] = useState<ItemCompleteProps[]>([]);
  const [listBranch, setListBranch] = useState<ItemCompleteProps[]>([]);
  const [dataFee, setDataFee] = useState<EBINQUIRY | undefined>(undefined);
  const [isBene, setIsBene] = useState<boolean>(false);
  const [accountBene, setAccountBene] = useState<PROFILE_BENE | undefined>();

  const { control, handleSubmit, setValue, getValues, clearErrors, trigger } = useForm<TransStockData>({
    defaultValues: {
      accountNo: "",
      accountName: "",
      stockID: "",
      stockName: "",
      branchCode: "",
      branchName: "",
      amount: "",
      description: "",
    },
    mode: "onChange",
  });

  const validateSchemeForm: ValidDataSchemeFormData = {
    accountNo: {
      required: {
        value: true,
        message: t("TRANFER_THONG_BAO_NHAP_THIEU_TAI_KHOAN_CHUNG_KHOAN"),
      },
    },
    accountName: {
      required: {
        value: true,
        message: t("TRANFER_VUI_LONG_NHAP_TEN_CHU_TAI_KHOAN_CONG_TY_CHUNG_KHOAN"),
      },
      validate: {
        regex: (value) => {
          const regex = regexCharacterMark();
          if (!regex.test(value)) {
            return t("TRANFER_VUI_LONG_NHAP_TEN_TAI_KHOAN_KHONG_CO_DAU").toString();
          }
          return true;
        },
      },
    },
    stockID: {
      required: {
        value: true,
        message: t("TRANFER_THONG_BAO_NHAP_THIEU_CONG_TY_CHUNG_KHOAN"),
      },
    },
    stockName: {
      required: {
        value: false,
        message: "",
      },
    },
    branchCode: {
      required: {
        value: true,
        message: t("TRANFER_VUI_LONG_CHON_CHI_NHANH_CONG_TY_CHUNG_KHOAN"),
      },
    },
    branchName: {
      required: {
        value: false,
        message: "",
      },
    },
    amount: {
      required: {
        value: true,
        message: t("TRANFER_THONG_BAO_NHAP_THIEU_SO_TIEN"),
      },
      validate: {
        amountMin: (value) => {
          if (Number(value) < 10000) {
            return t("COMMON_SO_TIEN_KHONG_DUOC_DUOI_10000D").toString();
          }
          return true;
        },
        amountMax: (value) => {
          if (validateAmount?.maxAmount && Number(value) > validateAmount?.maxAmount) {
            return validateAmount?.errorMaxDebit;
          }
          return true;
        },
      },
    },
    description: {
      required: {
        value: true,
        message: t("TRANFER_THONG_BAO_CHUA_NHAP_NOI_DUNG"),
      },
      validate: {
        regex: (value) => {
          const regex = regexCharacterMarkSpaceDot();
          if (!regex.test(value)) {
            return t("TRANFER_NOI_DUNG_CHUYEN_TIEN_KHONG_HOP_LE").toString();
          }
          return true;
        },
      },
    },
  };

  const handleRequestCreate = (value: TransStockData): void => {
    const dataUpdate: DataTransferCreate = {
      ACCOUNT_CREDIT: {
        ACCOUNTNO: value.accountNo,
        ACCOUNTTYPE: "10",
        ACCOUNTNAME: value.accountName,
      },
      STOCK: { value: value.stockID, label: value.stockName },
      BRANCH: { value: value.branchCode, label: value.branchName },
      AMOUNT: value.amount,
      DESCRIPTION: value.description,
      FEE_TYPE: Constant.TRANSFER.TRANSFER_FEE_TYPE.O,
      BENE: isBene && accountBene?.ACCOUNT_NUMBER?.length && accountBene?.ACCOUNT_NUMBER === value.accountNo ? accountBene : undefined,
    };
    console.log("dataUpdate: ", dataUpdate);
    requestCreate(dataUpdate);
  };

  const handlerSelectBene = (temp: PROFILE_BENE) => {
    closeModal();
    setIsBene(true);
    setAccountBene(temp);
    setValue("accountNo", temp?.ACCOUNT_NUMBER || "");
    setValue("accountName", temp?.BEN_NAME || "");
    clearErrors("description");
    const content = t("TRANFER_DESCRIPTION_CHUYEN_TIEN_CHUNG_KHOAN") + " " + temp?.ACCOUNT_NUMBER + " " + temp?.BEN_NAME;
    setValue("description", replaceEnterKey(replaceSpace(nonAccentVietnamese(content))));
    if (temp.COMPANY_NAME?.length) {
      setValue("stockID", temp?.COMPANY_NAME || "");
      const stockName = getStock(transferInfo, temp?.COMPANY_NAME).label || "";
      setValue("stockName", stockName);
      requestBranch(temp?.COMPANY_NAME);
    }
    clearErrors("accountNo");
  };

  const requestBranch = (code?: string) => {
    if (code?.length) {
      dispatch(fetchTransferStockBranchRequest(code));
    }
  };

  const checkSelectStock = () => {
    if (listStock.length) return false;
    return true;
  };

  const onChangeStock = (stock?: { label: string; value: string }) => {
    setValue("stockName", stock?.label || "");
    requestBranch(stock?.value);
  };

  const onClickBranch = () => {
    trigger("stockID");
  };

  const checkSelectBranch = () => {
    if (getValues("stockID").length && listBranch.length) return false;
    return true;
  };

  const onChangeBranch = (branch?: { label: string; value: string }) => {
    setValue("branchName", branch?.value || "");
  };

  const onPressCreate = () => {
    onSubmit();
    handleSubmit(handleRequestCreate).call(() => {});
  };

  const onPressCancel = () => {
    onCancel();
  };

  const onPressFeeDetail = async () => {
    onSubmit();
    const accountTrigger = await trigger("accountNo");
    const accountNameTrigger = await trigger("accountName");
    const amountTrigger = await trigger("amount");
    const stockTrigger = await trigger("stockID");
    const branchTrigger = await trigger("branchCode");
    if (accountTrigger && accountNameTrigger && stockTrigger && branchTrigger && amountTrigger) {
      const dataUpdate: DataTransferCreate = {
        ACCOUNT_CREDIT: {
          ACCOUNTNO: getValues("accountNo"),
          ACCOUNTTYPE: "10",
          ACCOUNTNAME: getValues("accountName"),
        },
        STOCK: { value: getValues("stockID"), label: getValues("stockName") },
        BRANCH: { value: getValues("branchCode"), label: getValues("branchName") },
        AMOUNT: getValues("amount"),
        DESCRIPTION: getValues("description") || t("TRANFER_DESCRIPTION_CHUYEN_TIEN_CHUNG_KHOAN"),
        FEE_TYPE: "0",
      };
      if (dataFee) setDataFee(undefined);
      requestMakeFee(dataUpdate);
    }
  };

  const onPressBeneAndAccount = () => {
    dispatch(resetListBene());
    showModal(<BeneModal templateType={Constant.TEMPLATE_TYPE.DEPOSIT_TO_STOCK_TRADE_ACCOUNT} onPressAcceptBene={handlerSelectBene} />);
  };

  // Số tài khoản chứng khoán
  const onBlurAccountNo = () => {
    const accountNo = getValues("accountNo") || "";
    if (accountNo.length) {
      clearErrors("description");
      const content = t("TRANFER_DESCRIPTION_CHUYEN_TIEN_CHUNG_KHOAN") + " " + accountNo + " " + getValues("accountName");
      setValue("description", replaceEnterKey(replaceSpace(nonAccentVietnamese(content))));
    }
    if (accountNo.length && accountBene?.ACCOUNT_NUMBER?.length && accountNo === accountBene?.ACCOUNT_NUMBER) {
      setIsBene(true);
    } else {
      setIsBene(false);
    }
  };

  // Tên chủ tài khoản
  const onBlurAccountName = () => {
    const accountName = getValues("accountName") || "";
    if (accountName.length) {
      clearErrors("description");
      const content = t("TRANFER_DESCRIPTION_CHUYEN_TIEN_CHUNG_KHOAN") + " " + getValues("accountNo") + " " + accountName;
      setValue("description", replaceEnterKey(replaceSpace(nonAccentVietnamese(content))));
    }
  };

  // fix/replace nội dung chuyển tiền
  const onBlurDescription = () => {
    const value = getValues("description") || "";
    setValue("description", replaceEnterKey(replaceSpace(nonAccentVietnamese(value.trim()))));
    trigger("description");
  };

  const styles = useMemo(
    () =>
      StyleSheet.create({
        container: { flex: 1 },
        fieldFromText: {
          color: colors.text,
          width: 200,
          fontWeight: "700",
          fontSize: 16,
        },
        buttonContainer: { width: 128, alignSelf: "center", backgroundColor: colors.notification },
        buttonSave: { width: 80, alignSelf: "center", backgroundColor: colors.primary, position: "absolute" },
        acceptButtonContainer: { width: 128, alignSelf: "center" },
        inputDisplay: { display: getValues("stockID").length && listBranch.length ? "flex" : "none" },
      }),
    [colors, getValues, listBranch.length],
  );

  const chooseAccountBene = (
    <TextInput.Icon name={() => <IconsYellowBanking name={"danhsachthuhuong"} size={28} />} size={28} onPress={onPressBeneAndAccount} />
  );

  const FieldForm = (
    <Block>
      <StackSpacing size="medium" />
      <Paragraph style={styles.fieldFromText}>{t("TRANFER_TAI_KHOAN_NHAN")}</Paragraph>
      <StackSpacing size="medium" />
    </Block>
  );

  const FormFee = <FormViewFee onClick={onPressFeeDetail} />;

  const FormInput = (
    <Block flex={1} bg={colors.backgroundForm} br={12} p={spacing.large}>
      <InputText
        rules={validateSchemeForm.accountNo}
        name="accountNo"
        control={control}
        inputProps={{ right: chooseAccountBene }}
        label={t("TRANFER_SO_TAI_KHOAN_CHUNG_KHOAN")}
        onBlur={onBlurAccountNo}
      />
      <InputText
        rules={validateSchemeForm.accountName}
        name="accountName"
        control={control}
        label={t("TRANFER_CHU_TAI_KHOAN")}
        onBlur={onBlurAccountName}
      />
      <InputAutoComplete
        title={t("TRANFER_CHON_CONG_TY_CHUNG_KHOAN")}
        options={listStock}
        name="stockID"
        rules={validateSchemeForm.stockID}
        control={control}
        onDismiss={onChangeStock}
        label={t("TRANFER_CONG_TY_CHUNG_KHOAN")}
        checkSelect={checkSelectStock()}
        showSearch={true}
      />
      <InputAutoComplete
        title={t("TRANFER_CHON_CHI_NHANH")}
        options={listBranch}
        name="branchCode"
        rules={validateSchemeForm.branchCode}
        control={control}
        style={styles.inputDisplay}
        onDismiss={onChangeBranch}
        label={t("TRANFER_CHI_NHANH")}
        onClick={onClickBranch}
        checkSelect={checkSelectBranch()}
        showSearch={true}
      />
      <InputTextCurrency
        name="amount"
        keyboardType={"numeric"}
        control={control}
        rules={validateSchemeForm.amount}
        label={t("TRANFER_SO_TIEN")}
        rightCurrency={t("COMMON_VND")}
      />
      <InputTextMultiline
        name="description"
        control={control}
        rules={validateSchemeForm.description}
        label={t("TRANFER_NOI_DUNG_CHUYEN_TIEN")}
        note={t("COMMON_NOTE_DESCRIPTION")}
        onBlur={onBlurDescription}
        inputProps={{ multiline: true, maxLength: 100 }}
        rightAffix={true}
      />
      {FormFee}
    </Block>
  );

  const ButtonForm = (
    <Block row middle pv={spacing.huge}>
      <Block w={isDesktop ? 200 : 0} />
      <Button onPress={onPressCancel} mode="contained" style={styles.buttonContainer}>
        {t("COMMON_HUY")}
      </Button>
      <QueueSpacing size="large" />
      <Button mode="contained" style={styles.acceptButtonContainer} onPress={onPressCreate}>
        {t("COMMON_TIEP_TUC")}
      </Button>
    </Block>
  );

  // PSI
  useEffect(() => {
    if (transferInfo) {
      setListStock(getListStock(transferInfo));
    }
  }, [transferInfo]);

  // branch
  useEffect(() => {
    if (branchStore) {
      console.log("branchStore: ", branchStore);
      setListBranch(getStockListBranch(branchStore));
    }
  }, [branchStore]);

  // branch bene
  useEffect(() => {
    if (accountBene?.COMPANY_NAME?.length && accountBene?.BRANCH_NAME?.length && listBranch.length) {
      const branchArr = listBranch.filter((item) => {
        return item.value === accountBene?.BRANCH_NAME;
      });
      if (branchArr.length) {
        setValue("branchCode", branchArr[0].value || "");
        setValue("branchName", branchArr[0].label || "");
      }
    }
  }, [accountBene, listBranch, setValue]);

  useEffect(() => {
    // lấy hạn mức gd ngày/ số dư khả dụng
    const limitRemain = transferInfo?.USERPROFILE?.LIMIT_REMAIN || limitRemainStore;
    const { maxAmount, errorMaxDebit } = amountConfig(accountDebit, limitRemain, getValues("accountNo"));
    setValidateAmount({ maxAmount, errorMaxDebit });
  }, [limitRemainStore, transferInfo, accountDebit, getValues]);

  useEffect(() => {
    if (validateAmount?.errorMaxDebit?.length && getValues("amount").length) trigger("amount");
  }, [getValues, trigger, validateAmount]);

  useEffect(() => {
    if (!accountBene?.ACCOUNT_NUMBER?.length && transferStockStore.dataCreate?.BENE?.ACCOUNT_NUMBER) {
      setAccountBene(transferStockStore.dataCreate?.BENE);
      if (transferStockStore.dataCreate?.BENE?.ACCOUNT_NUMBER === getValues("accountNo")) setIsBene(true);
    }
  }, [accountBene, getValues, transferStockStore]);

  useEffect(() => {
    if (responseMakeFee?.TRANSFER) {
      setDataFee(responseMakeFee.TRANSFER);
    }
  }, [responseMakeFee]);

  // // show Phí
  useEffect(() => {
    if (dataFee) {
      showModal(<FeeDetailModalNew data={dataFee} feeType={Constant.TRANSFER.TRANSFER_FEE_TYPE.O} />);
      setDataFee(undefined);
    }
  }, [accountDebit, getValues, dataFee, showModal]);

  return (
    <>
      <Block row={isDesktop ? true : false} pt={spacing.medium}>
        {FieldForm}
        {FormInput}
      </Block>
      {ButtonForm}
    </>
  );
};

export default FormTransStock;
